import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errDetail } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { InviteDialog } from "../../components/iam/InviteDialog";
import { ReassignDialog } from "../../components/iam/ReassignDialog";
import { UserPlus, Search, Download, Shuffle, ChevronLeft, ChevronRight, Bookmark, SlidersHorizontal, X, Pencil, RefreshCw } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700", invited: "bg-blue-50 text-blue-700",
  suspended: "bg-amber-50 text-amber-700", deactivated: "bg-gray-100 text-gray-500",
};

export default function Users() {
  const { t, picklists } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState({ items: [], total: 0, pages: 1 });
  const [roles, setRoles] = useState([]);
  const [plants, setPlants] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    role_id: "", plant_id: "", segment: "", status: params.get("status") || "",
    auth_method: "", pending_scope: params.get("pending_scope") ? "1" : "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sel, setSel] = useState([]);
  const [invite, setInvite] = useState(false);
  const [reassign, setReassign] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [views, setViews] = useState(() => JSON.parse(localStorage.getItem("lf_iam_views") || "[]"));

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: pageSize });
    if (q) p.set("q", q);
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/iam/users?${p.toString()}`).then((r) => setRows(r.data)).catch((e) => toast.error(errDetail(e)));
  }, [page, pageSize, q, filters]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  useEffect(() => {
    api.get("/iam/roles").then((r) => setRoles(r.data || [])).catch(() => {});
    api.get("/iam/plants?page_size=200").then((r) => setPlants(r.data.items || [])).catch(() => {});
  }, []);

  const allUsers = useMemo(() => rows.items.map((u) => ({ id: u.id, name: u.name })), [rows.items]);
  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v === "any" ? "" : v })); setPage(1); };
  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const runBulk = async (payload) => {
    try {
      const { data } = await api.post("/iam/users/bulk", { user_ids: sel, ...payload });
      toast.success(`${data.updated} ✓`);
      setSel([]); setBulk(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const saveView = () => {
    const name = window.prompt(t("iam_save_view"));
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const next = views.some((v) => v.name === trimmed)
      ? views.map((v) => (v.name === trimmed ? { name: trimmed, q, filters } : v))
      : [...views, { name: trimmed, q, filters }];
    setViews(next);
    localStorage.setItem("lf_iam_views", JSON.stringify(next));
  };

  const persistViews = (next) => {
    setViews(next);
    localStorage.setItem("lf_iam_views", JSON.stringify(next));
  };

  const deleteView = (name) => {
    if (!window.confirm(t("iam_delete_view_confirm"))) return;
    persistViews(views.filter((v) => v.name !== name));
  };

  const renameView = (name) => {
    const next = window.prompt(t("iam_rename_view"), name);
    if (!next) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === name) return;
    if (views.some((v) => v.name === trimmed)) { toast.error(t("iam_view_exists")); return; }
    persistViews(views.map((v) => (v.name === name ? { ...v, name: trimmed } : v)));
  };

  const updateView = (name) => {
    persistViews(views.map((v) => (v.name === name ? { ...v, q, filters } : v)));
    toast.success(t("iam_view_updated") + " ✓");
  };

  const exportCsv = () => {
    api.get("/iam/users/export", { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "users.csv"; a.click();
    }).catch(() => {});
  };

  return (
    <div className="space-y-4" data-testid="iam-users-page">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }}>{t("iam_users")}</h1>
          <p className="text-xs text-gray-500">{rows.total} {t("iam_users")}</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setReassign(true)} data-testid="open-reassign-btn">
            <Shuffle className="h-3.5 w-3.5 mr-1" />{t("iam_reassign")}
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="users-export-btn">
            <Download className="h-3.5 w-3.5 mr-1" />{t("iam_export_csv")}
          </Button>
          <Button size="sm" className="bg-[#E8871E] hover:bg-[#d1751a] text-white font-semibold rounded-full"
            onClick={() => setInvite(true)} data-testid="invite-user-btn">
            <UserPlus className="h-4 w-4 mr-1" />{t("iam_invite_user")}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-8 h-9"
              placeholder={t("iam_search_users")} data-testid="users-search-input" />
          </div>
          <Button variant="outline" size="sm" className="sm:hidden" onClick={() => setShowFilters((s) => !s)}
            data-testid="users-filter-toggle"><SlidersHorizontal className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={saveView} data-testid="users-save-view-btn" className="hidden sm:flex">
            <Bookmark className="h-3.5 w-3.5 mr-1" />{t("iam_save_view")}
          </Button>
        </div>

        <div className={`${showFilters ? "grid" : "hidden"} sm:grid grid-cols-2 lg:grid-cols-5 gap-2`} data-testid="users-filters">
          <Select value={filters.role_id || "any"} onValueChange={(v) => setF("role_id", v)}>
            <SelectTrigger className="h-9 text-xs" data-testid="users-filter-role"><SelectValue placeholder={t("role")} /></SelectTrigger>
            <SelectContent><SelectItem value="any">{t("role")}: {t("all")}</SelectItem>
              {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.plant_id || "any"} onValueChange={(v) => setF("plant_id", v)}>
            <SelectTrigger className="h-9 text-xs" data-testid="users-filter-plant"><SelectValue placeholder={t("iam_plants")} /></SelectTrigger>
            <SelectContent><SelectItem value="any">{t("iam_plants")}: {t("all")}</SelectItem>
              {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.segment || "any"} onValueChange={(v) => setF("segment", v)}>
            <SelectTrigger className="h-9 text-xs" data-testid="users-filter-segment"><SelectValue placeholder={t("segment")} /></SelectTrigger>
            <SelectContent><SelectItem value="any">{t("segment")}: {t("all")}</SelectItem>
              {(picklists?.segments || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status || "any"} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger className="h-9 text-xs" data-testid="users-filter-status"><SelectValue placeholder={t("status")} /></SelectTrigger>
            <SelectContent><SelectItem value="any">{t("status")}: {t("all")}</SelectItem>
              {["active", "invited", "suspended", "deactivated"].map((s) => (
                <SelectItem key={s} value={s}>{t(`iam_status_${s}`)}</SelectItem>))}</SelectContent>
          </Select>
          <Select value={filters.auth_method || "any"} onValueChange={(v) => setF("auth_method", v)}>
            <SelectTrigger className="h-9 text-xs" data-testid="users-filter-auth"><SelectValue placeholder={t("iam_auth_method")} /></SelectTrigger>
            <SelectContent><SelectItem value="any">{t("iam_auth_method")}: {t("all")}</SelectItem>
              <SelectItem value="password">Passwort</SelectItem><SelectItem value="sso">SSO</SelectItem></SelectContent>
          </Select>
        </div>

        {views.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="users-saved-views">
            {views.map((v) => (
              <span key={v.name} data-testid={`users-view-${v.name}`}
                className="inline-flex items-center rounded-full text-[11px] border border-gray-200 bg-white overflow-hidden">
                <button onClick={() => { setQ(v.q); setFilters(v.filters); setPage(1); }}
                  className="pl-2.5 pr-1.5 h-7 hover:text-[#E8871E]" data-testid={`users-view-apply-${v.name}`}>
                  {v.name}
                </button>
                <button onClick={() => updateView(v.name)} title={t("iam_view_update")}
                  data-testid={`users-view-update-${v.name}`}
                  className="h-7 px-1 text-gray-400 hover:text-[#1B2A4A] border-l border-gray-100">
                  <RefreshCw className="h-3 w-3" />
                </button>
                <button onClick={() => renameView(v.name)} title={t("iam_rename_view")}
                  data-testid={`users-view-rename-${v.name}`}
                  className="h-7 px-1 text-gray-400 hover:text-[#1B2A4A] border-l border-gray-100">
                  <Pencil className="h-3 w-3" />
                </button>
                <button onClick={() => deleteView(v.name)} title={t("delete")}
                  data-testid={`users-view-delete-${v.name}`}
                  className="h-7 px-1.5 text-gray-400 hover:text-red-600 border-l border-gray-100">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {sel.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center rounded-lg bg-amber-50 border border-amber-200 px-3 py-2"
            data-testid="users-bulk-bar">
            <span className="text-xs font-semibold text-[#1B2A4A]">{sel.length} {t("iam_selected")}</span>
            <Button size="sm" variant="outline" onClick={() => setBulk("assign_plants")} data-testid="bulk-plants-btn">{t("iam_assign_plants")}</Button>
            <Button size="sm" variant="outline" onClick={() => setBulk("assign_segments")} data-testid="bulk-segments-btn">{t("iam_assign_segments")}</Button>
            <Button size="sm" variant="outline" onClick={() => setBulk("change_role")} data-testid="bulk-role-btn">{t("iam_change_role")}</Button>
            <Button size="sm" variant="outline" onClick={() => runBulk({ action: "resend_invite" })} data-testid="bulk-resend-btn">{t("iam_resend_invite")}</Button>
            <Button size="sm" variant="outline" className="text-amber-700" onClick={() => runBulk({ action: "suspend" })} data-testid="bulk-suspend-btn">{t("iam_suspend")}</Button>
          </div>
        )}
      </div>

      {/* desktop table */}
      <div className="hidden md:block rounded-xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/70 border-b border-gray-100 text-xs text-gray-500">
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 text-left">{t("name")}</th>
              <th className="px-3 py-2 text-left">{t("role")}</th>
              <th className="px-3 py-2 text-left">{t("iam_plants")}</th>
              <th className="px-3 py-2 text-left">{t("iam_segments")}</th>
              <th className="px-3 py-2 text-left">{t("status")}</th>
              <th className="px-3 py-2 text-left">{t("iam_last_login")}</th>
              <th className="px-3 py-2 text-left">{t("iam_auth_method")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.items.map((u) => (
              <tr key={u.id} className="border-b border-gray-50 hover:bg-amber-50/30 cursor-pointer"
                data-testid={`user-row-${u.email}`} onClick={() => navigate(`/iam/users/${u.id}`)}>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={sel.includes(u.id)} onCheckedChange={() => toggleSel(u.id)}
                    data-testid={`user-select-${u.email}`} />
                </td>
                <td className="px-3 py-2">
                  <p className="font-medium text-[#1B2A4A]">{u.name}{u.pending_scope && <span className="ml-1 text-[10px] text-[#E8871E]">● {t("iam_pending_scope_badge")}</span>}</p>
                  <p className="text-[11px] text-gray-400">{u.email}</p>
                </td>
                <td className="px-3 py-2 text-xs">{u.role_name}</td>
                <td className="px-3 py-2 text-xs">
                  {u.plant_count === "all" ? <Badge variant="outline" className="text-[10px]">{t("iam_all_plants")}</Badge>
                    : <span className="font-mono text-[11px]">{u.plant_codes?.join(", ") || "–"}</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {u.segment_list === "all" ? <Badge variant="outline" className="text-[10px]">{t("iam_all_segments")}</Badge>
                    : (u.segment_list || []).join(", ") || "–"}
                </td>
                <td className="px-3 py-2">
                  <Badge className={`${STATUS_STYLE[u.status] || "bg-gray-100"} hover:bg-inherit text-[10px]`}>
                    {t(`iam_status_${u.status || "active"}`)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-[11px] text-gray-500 font-mono">{(u.last_login_at || "").slice(0, 10) || "–"}</td>
                <td className="px-3 py-2 text-[11px]">
                  {u.auth_method === "sso" ? <Badge className="bg-blue-50 text-blue-700 hover:bg-inherit text-[10px]">SSO</Badge> : "Passwort"}
                </td>
              </tr>
            ))}
            {!rows.items.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-400">{t("iam_no_users")}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.items.map((u) => (
          <div key={u.id} className="rounded-xl border border-gray-200 bg-white p-3" data-testid={`user-card-${u.email}`}>
            <div className="flex items-start gap-2">
              <Checkbox checked={sel.includes(u.id)} onCheckedChange={() => toggleSel(u.id)} className="mt-1" />
              <Link to={`/iam/users/${u.id}`} className="flex-1">
                <p className="font-semibold text-[#1B2A4A]">{u.name}</p>
                <p className="text-[11px] text-gray-400">{u.email}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <Badge variant="outline" className="text-[10px]">{u.role_name}</Badge>
                  <Badge className={`${STATUS_STYLE[u.status] || "bg-gray-100"} hover:bg-inherit text-[10px]`}>
                    {t(`iam_status_${u.status || "active"}`)}
                  </Badge>
                  {u.plant_count === "all"
                    ? <Badge variant="outline" className="text-[10px]">{t("iam_all_plants")}</Badge>
                    : (u.plant_codes || []).map((c) => <Badge key={c} variant="outline" className="text-[10px] font-mono">{c}</Badge>)}
                </div>
              </Link>
            </div>
          </div>
        ))}
        {!rows.items.length && <p className="text-center text-sm text-gray-400 py-6">{t("iam_no_users")}</p>}
      </div>

      <div className="flex items-center gap-2 justify-between">
        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
          <SelectTrigger className="h-8 w-24 text-xs" data-testid="users-pagesize"><SelectValue /></SelectTrigger>
          <SelectContent>{[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-xs">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="users-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span data-testid="users-page-info">{page} / {rows.pages}</span>
          <Button size="sm" variant="outline" disabled={page >= rows.pages} onClick={() => setPage((p) => p + 1)} data-testid="users-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <InviteDialog open={invite} onOpenChange={setInvite} roles={roles} onDone={load} />
      <ReassignDialog open={reassign} onOpenChange={setReassign} users={allUsers} onDone={load} />

      <BulkDialog kind={bulk} onClose={() => setBulk(null)} onRun={runBulk} roles={roles} plants={plants} />
    </div>
  );
}

function BulkDialog({ kind, onClose, onRun, roles, plants }) {
  const { t, picklists } = useApp();
  const [val, setVal] = useState([]);
  const [roleId, setRoleId] = useState("");
  useEffect(() => { setVal([]); setRoleId(""); }, [kind]);
  if (!kind) return null;
  const toggle = (v) => setVal((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v]);
  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle>{t(kind === "assign_plants" ? "iam_assign_plants" : kind === "assign_segments" ? "iam_assign_segments" : "iam_change_role")}</DialogTitle>
          <DialogDescription>{t("iam_bulk_dialog_help")}</DialogDescription>
        </DialogHeader>
        {kind === "change_role" ? (
          <Select value={roleId} onValueChange={setRoleId}>
            <SelectTrigger data-testid="bulk-role-select"><SelectValue placeholder="–" /></SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-50 rounded-md border border-gray-100">
            {(kind === "assign_plants" ? plants.map((p) => [p.id, `${p.code} · ${p.name}`]) : (picklists?.segments || []).map((s) => [s, s]))
              .map(([id, label]) => (
                <label key={id} className="px-3 py-2 flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={val.includes(id)} onCheckedChange={() => toggle(id)} />
                  {label}
                </label>
              ))}
          </div>
        )}
        <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" data-testid="bulk-apply-btn"
          onClick={() => onRun(kind === "change_role" ? { action: "change_role", role_id: roleId }
            : kind === "assign_plants" ? { action: "assign_plants", plant_ids: val }
              : { action: "assign_segments", segments: val })}>
          {t("save")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
